<script lang="ts">
import Skeleton from '@/components/ui/skeleton/Skeleton.vue'
import { bus as layoutBus } from '@/layouts/default.vue'

import PluginsMenu from '~/components/App/NewHeader/PluginsMenu/index.vue'
import TheDailyBiteMenu from '~/components/App/NewHeader/TheDailyBite/index.vue'
import DesktopAppMenu from '~/components/App/NewHeader/DesktopAppMenu/index.vue'
import PromptLibraryMenu from '~/components/App/NewHeader/PromptLibraryMenu/index.vue'
import { useTopicsQuery } from '~~/src/composables/query/topic/useTopicsQuery'
import { LINKS } from '@/utils/constants'
export const links: Record<string, string> = {
  'Roadmap': LINKS.roadmap,
  'Feature Requests': LINKS.featureRequests,
  'Support': LINKS.support,
}

export default defineComponent({
  name: 'AppHeader',
})
</script>

<script setup lang="ts">
const { colors } = useTheme()
const isSSR = process.server

const user = useUser()
const { data: topics } = useTopicsQuery({
  query: {},
})

const { mobileHeaderHeight } = useLayout()

const tabletNavRef = ref<HTMLDivElement>()
const [showTabletNav, toggleTabletNav] = useFloatingElement(tabletNavRef)

const desktopUserChipRef = ref<HTMLDivElement>()
const topicNavExpanded = ref(true)

onMounted(() => {
  layoutBus.on((e) => {
    if (e === 'expand-navigation')
      topicNavExpanded.value = true
    else if (e === 'collapse-navigation')
      topicNavExpanded.value = false
  })
})

function handleLogoClick() {
  layoutBus.emit('close-overlay')
  clearError()
}

function handleLoginClick() {
  if (!user.value)
    return router.push(useGetLoginURL())
}

const filters = useFilters()
const sortedTopics = computed(() => {
  const typeId = filters.value?.type_ids?.[0] || 1
  const isTextFilter = typeId === PROMPT_TYPES.Text
  return topics.value
    ?.filter((topic) => {
      const includes = topic.typeIds.includes(typeId)
      const havesome = isTextFilter ? topic.nTextPrompts > 0 : topic.nVisualPrompts > 0
      return includes && havesome
    })
})

type Position = 'left' | 'right' | 'both' | 'none'
const fadePosition = ref<Position>('right')
const suggestedTopicRef = ref<HTMLUListElement>()
function handleTopicListScroll() {
  if (!suggestedTopicRef.value)
    return

  const { scrollLeft, scrollWidth, clientWidth } = suggestedTopicRef.value

  const maxScrollLeft = scrollWidth - clientWidth

  if (maxScrollLeft < 300)
    fadePosition.value = 'none'
  else if (scrollLeft > 0 && scrollLeft < maxScrollLeft)
    fadePosition.value = 'both'
  else if (scrollLeft > 0)
    fadePosition.value = 'left'
  else if (scrollLeft < maxScrollLeft)
    fadePosition.value = 'right'
  else
    fadePosition.value = 'none'
}

watch(sortedTopics, () => {
  // Needs 1ms timeout for it to work
  setTimeout(() => {
    if (suggestedTopicRef.value)
      suggestedTopicRef.value.scrollLeft = 0

    handleTopicListScroll()
  }, 1)
})

const router = useRouter()

router.afterEach(() => {
  closeAllHeaderMenus()
})

const openMenuState = useHeaderMenusActive()
</script>

<template>
  <div>
    <NuxtLoadingIndicator
      color="#f994e7"
      :height="3"
      :throttle="0"
      :duration="3000"
      :delay="0"
      :estimated-progress="true"
      class="!opacity-100"
    />
    <header
      id="header-component"
      border="b light-bluish" bg-white col
      :data-header-height="mobileHeaderHeight"
      @mouseleave="closeAllHeaderMenus()"
    >
      <div
        w-full relative
        bg-white
        flex justify-between
        p="x-5 y-[10px]"
        z-11
        class="md:gap-5 min-h-[60px] md:min-h-[61px]"
      >
        <div flex="~ 1 items-center" class="gap-2 md:gap-5">
          <AppHeaderEnterprise @logo-click="handleLogoClick" />

          <AppHeaderSearch />

          <TransitionFade>
            <div class="hidden md:flex lg:hidden" center>
              <div v-if="isSSR">
                <div i-mdi:menu w-7 h-7 cursor-pointer />
              </div>
              <ClientOnly>
                <Popper :show="showTabletNav">
                  <div i-mdi:menu w-7 h-7 cursor-pointer @click="toggleTabletNav()" />

                  <template #content>
                    <AppFloatingMenu ref="tabletNavRef">
                      <nav>
                        <ul center>
                          <AppFloatingMenuItem>
                            <button center gap="2px" @click="toggleHeaderMenu('promptLibrary')">
                              <Text :color="openMenuState.promptLibrary ? colors.softBlack : colors.bluishDarker" :bold="openMenuState.promptLibrary">
                                Prompt Library
                              </Text>
                            </button>
                          </AppFloatingMenuItem>

                          <AppFloatingMenuItem>
                            <button center gap="2px" @click="toggleHeaderMenu('desktopApp')">
                              <Text :color="openMenuState.desktopApp ? colors.softBlack : colors.bluishDarker" :bold="openMenuState.desktopApp">
                                Desktop App
                              </Text>
                            </button>
                          </AppFloatingMenuItem>

                          <AppFloatingMenuItem>
                            <button center gap="2px" @click="toggleHeaderMenu('theDailyBite')">
                              <Text :color="openMenuState.theDailyBite ? colors.softBlack : colors.bluishDarker" :bold="openMenuState.theDailyBite">
                                The Daily Bite
                              </Text>
                            </button>
                          </AppFloatingMenuItem>

                          <AppFloatingMenuItem>
                            <button center gap="2px" @click="toggleHeaderMenu('plugins')">
                              <Text :color="openMenuState.plugins ? colors.softBlack : colors.bluishDarker" :bold="openMenuState.plugins">
                                Plugins
                              </Text>
                            </button>
                          </AppFloatingMenuItem>
                        </ul>
                      </nav>
                    </AppFloatingMenu>
                  </template>
                </Popper>
              </ClientOnly>
            </div>
          </TransitionFade>
        </div>

        <div flex items-center gap-2 relative>
          <div center gap-5>
            <nav class="hidden lg:flex" center gap-5>
              <AppHeaderMenuLinks />
            </nav>
          </div>
          <div class="min-w-[65px] h-10 flex items-center justify-end">
            <PopupMenu v-if="user" :interactive="true" placement="bottom-end">
              <AppButton
                variant="secondary"
                title="User navigation"
                h-10
                :button-style="{
                  padding: '5px !important',
                  width: '40px',
                }"
                @click="handleLoginClick()"
              >
                <div flex justify-center items-center gap-2>
                  <div ref="desktopUserChipRef" w-8>
                    <AppHeaderSellerProfileNotification />
                    <img
                      loading="lazy"
                      :src="optimizeImage(user.avatar, {
                        width: 32 * 1.6,
                        height: 32 * 1.6,
                      })"
                      referrerpolicy="no-referrer"
                      w-8 h-8 rounded-full object-cover object-center
                    >
                  </div>
                </div>
              </AppButton>

              <template #content="{ close }">
                <AppHeaderMenuUser v-if="user" :user="user" @close="close" />
              </template>
            </PopupMenu>

            <div v-if="!user" class="profile-loader hidden border w-10 h-10 border-bluish p-[3px] rounded-full">
              <Skeleton class="w-full h-full rounded-full" />
            </div>
            <AppButton
              v-if="!user"
              variant="secondary"
              title="User navigation"
              class="login-button"
              h-10
              @click="handleLoginClick()"
            >
              Log in
            </AppButton>
          </div>
        </div>

        <div class="hidden md:block" absolute top-full left-0 w-full z="at-md:10">
          <PluginsMenu />
          <TheDailyBiteMenu />
          <DesktopAppMenu />
          <PromptLibraryMenu />
        </div>
      </div>
    </header>
  </div>
</template>
